import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  Notice,
  SearchBox,
  Select,
  Spinner,
  Table,
  TableWrap,
  Tabs,
  Toolbar,
  useToast,
} from 'iboon-ui';

import { CustomFields } from '../CustomFields';
import { api, type Asset, type AssetState, type Category, type Location } from '../api';

/**
 * Everything tracked one by one.
 *
 * The tabs are states rather than categories, because the question somebody
 * opens this with is almost always "what is free" or "who has the spare
 * monitor" -- not "show me all monitors". Category is a filter underneath for
 * when it is the other question.
 */
const STATE_TONE: Record<AssetState, 'brand' | 'accent' | 'danger' | 'neutral'> = {
  available: 'brand',
  assigned: 'accent',
  repair: 'neutral',
  lost: 'danger',
  retired: 'neutral',
};

const TABS = [
  { id: '', label: 'Everything' },
  { id: 'available', label: 'Free' },
  { id: 'assigned', label: 'Out' },
  { id: 'repair', label: 'Being fixed' },
  { id: 'lost', label: 'Lost' },
  { id: 'retired', label: 'Retired' },
] as const;

export function Assets() {
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState('');
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async (filterState: string, q: string) => {
    const params = new URLSearchParams();
    if (filterState) params.set('state', filterState);
    if (q.trim()) params.set('q', q.trim());
    const result = await api.get<{ assets: Asset[]; counts: Record<string, number> }>(
      `/api/assets?${params}`,
    );
    setAssets(result.assets);
    setCounts(result.counts);
  }, []);

  useEffect(() => {
    api.get<{ categories: Category[] }>('/api/categories').then((r) => setCategories(r.categories));
  }, []);

  useEffect(() => {
    // Debounced, because this fires on every keystroke and each one is a query
    // across the whole register.
    const timer = setTimeout(() => {
      setAssets(null);
      load(state, query).catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : 'Could not load the register.'),
      );
    }, 200);
    return () => clearTimeout(timer);
  }, [state, query, load]);

  const total = useMemo(
    () => Object.values(counts).reduce((sum, n) => sum + n, 0),
    [counts],
  );

  if (error) {
    return (
      <Notice tone="danger" title="Could not load the register">
        {error}
      </Notice>
    );
  }

  return (
    <>
      <Toolbar>
        <Tabs
          items={TABS.map((tab) => ({
            id: tab.id,
            label: tab.label,
            badge: tab.id ? (counts[tab.id] ?? 0) : total,
          }))}
          value={state}
          onChange={setState}
          label="State"
        />
        <SearchBox
          value={query}
          onValueChange={setQuery}
          placeholder="Name, tag, serial, or who has it…"
        />
        <Button variant="primary" onClick={() => setAdding(true)}>
          Add an asset
        </Button>
      </Toolbar>

      {!assets ? (
        <Spinner />
      ) : assets.length === 0 ? (
        <EmptyState
          title={query || state ? 'Nothing matches' : 'The register is empty'}
          description={
            query || state
              ? 'No asset matches that.'
              : 'Add the first thing you own, or a category to put things in.'
          }
        />
      ) : (
        <Card padded={false}>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Category</th>
                  <th>State</th>
                  <th>Who has it</th>
                  <th>Where</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((asset) => (
                  <tr key={asset.id}>
                    <td className="tcell-name">
                      <Link to={`/assets/${asset.id}`}>{asset.name}</Link>
                      <div className="sub">
                        {[asset.assetTag, asset.make, asset.model].filter(Boolean).join(' · ') ||
                          asset.serialNumber ||
                          '—'}
                      </div>
                    </td>
                    <td>{asset.categoryName}</td>
                    <td>
                      <Badge tone={STATE_TONE[asset.state]}>{asset.state}</Badge>
                    </td>
                    <td>
                      {asset.heldByName ? (
                        <>
                          {asset.heldByName}
                          <div className="sub">{asset.heldByEmail}</div>
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="muted">{asset.locationName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      {adding && (
        <AddAsset
          categories={categories.filter((c) => c.tracking === 'tracked')}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            void load(state, query);
          }}
        />
      )}
    </>
  );
}

function AddAsset({
  categories,
  onClose,
  onAdded,
}: {
  categories: Category[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [form, setForm] = useState({
    categoryId: categories[0]?.id ?? '',
    name: '',
    make: '',
    model: '',
    serialNumber: '',
    assetTag: '',
    locationId: '',
  });
  const [fields, setFields] = useState<Record<string, unknown>>({});
  const [locations, setLocations] = useState<Location[]>([]);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api
      .get<{ locations: Location[] }>('/api/locations')
      .then((r) => setLocations(r.locations))
      .catch(() => setLocations([]));
  }, []);

  const chosen = categories.find((category) => category.id === form.categoryId);

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((was) => ({ ...was, [key]: event.target.value }));

  async function save() {
    setBusy(true);
    try {
      // Only the values this category actually asks for. Switching category
      // mid-form would otherwise carry the previous one's answers across and
      // store them under keys nothing reads.
      const keep = new Set((chosen?.fieldSchema ?? []).map((field) => field.key));
      const cleaned = Object.fromEntries(
        Object.entries(fields).filter(([key, value]) => keep.has(key) && value !== ''),
      );
      await api.post('/api/assets', { ...form, fields: cleaned });
      toast.show(`${form.name} is in the register.`);
      onAdded();
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not add it.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open size="sm" title="Add an asset" onClose={onClose}>
      {categories.length === 0 ? (
        // Said plainly rather than showing a form that cannot be submitted:
        // an asset needs a category, and there are none yet.
        <Notice tone="accent" title="No categories yet">
          An asset belongs to a category — Laptops, Monitors, Docks. Add one first, and say whether
          things in it are tracked one by one or counted in bulk.
        </Notice>
      ) : (
        <>
          <Field label="Category" required>
            <Select value={form.categoryId} onChange={set('categoryId')}>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="What is it" required hint="How somebody would ask for it: “ThinkPad X1”.">
            <Input value={form.name} onChange={set('name')} />
          </Field>
          <Field label="Make">
            <Input value={form.make} onChange={set('make')} placeholder="Lenovo" />
          </Field>
          <Field label="Model">
            <Input value={form.model} onChange={set('model')} placeholder="X1 Carbon Gen 11" />
          </Field>
          <Field label="Serial number" hint="The manufacturer's. Leave it blank if there is none.">
            <Input value={form.serialNumber} onChange={set('serialNumber')} />
          </Field>
          <Field label="Asset tag" hint="Yours — what is written on the label.">
            <Input value={form.assetTag} onChange={set('assetTag')} placeholder="IB-LT-01" />
          </Field>
          {locations.length > 0 && (
            <Field label="Where it is">
              <Select value={form.locationId} onChange={set('locationId')}>
                <option value="">—</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {/* Whatever this category says is worth knowing about its kind. */}
          <CustomFields
            schema={chosen?.fieldSchema ?? []}
            values={fields}
            onChange={setFields}
          />

          <div className="row-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
            <Button variant="primary" disabled={busy || !form.name} onClick={() => void save()}>
              {busy ? 'Adding…' : 'Add it'}
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
